"use client";

import { useEffect } from "react";

/**
 * Stamps `<html data-input-modality="pointer" | "keyboard">` from the user's
 * LAST interaction, so CSS can show focus rings only to keyboard users.
 *
 * Why: browsers paint `:focus-visible` on anything focused BY SCRIPT — the
 * focus trap's `focusFirst()` when a modal opens, `inputRef.focus()` when a
 * dropdown opens — so a mouse click on the settings gear ended with a 2px
 * white ring around the first button in the dialog. The ring is a keyboard
 * affordance; it has no business appearing after a click. Keyboard users
 * keep it: the first Tab / arrow key flips the attribute back.
 *
 * Mounted once in the root layout. Listeners are capture-phase so a handler
 * that stops propagation cannot hide an interaction from us.
 */
const KEYBOARD_NAV_KEYS = new Set([
  "Tab",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Home",
  "End",
  "PageUp",
  "PageDown",
]);

export default function InputModality() {
  useEffect(() => {
    const root = document.documentElement;
    const set = (mode: "pointer" | "keyboard") => {
      if (root.dataset.inputModality !== mode) root.dataset.inputModality = mode;
    };
    // Assume pointer until a navigation key says otherwise — the very first
    // programmatic focus must not ring either.
    set("pointer");

    const onPointer = () => set("pointer");
    const onKey = (e: KeyboardEvent) => {
      // Typing into a field is not navigation; only the keys that MOVE focus
      // switch modes, otherwise the input the user is typing in would ring.
      if (KEYBOARD_NAV_KEYS.has(e.key)) set("keyboard");
    };

    window.addEventListener("pointerdown", onPointer, true);
    window.addEventListener("mousedown", onPointer, true);
    window.addEventListener("touchstart", onPointer, { capture: true, passive: true });
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("pointerdown", onPointer, true);
      window.removeEventListener("mousedown", onPointer, true);
      window.removeEventListener("touchstart", onPointer, true);
      window.removeEventListener("keydown", onKey, true);
    };
  }, []);

  return null;
}
